<template>
    <div class="point-list-wrapper">
        <template v-if="pointsOfSale.length > 0">
            <ul
                ref="pointList"
                class="point-list no-bullet"
                :class="{ 'show-more-results': moreResultsVisible }"
            >
                <li v-for="(pointOfSale, i) in pointsOfSale" :key="`point-${i}`" class="point-item">
                    <PointOfSaleItem
                        :is-selected="pointOfSale.index === selectedPointIndex"
                        :point-of-sale="pointOfSale"
                        :messages="pointItemMessages"
                        :meta="meta"
                        :is-hidden="!moreResultsVisible && i >= visibleItemsCount"
                        @locatePoint="handleLocatePoint"
                    />
                </li>
            </ul>
            <button
                v-if="pointsOfSale.length > 4"
                type="button"
                class="show-more-button"
                :class="{ 'is-active': moreResultsVisible }"
                @click="showMoreResults"
            >
                <template v-if="moreResultsVisible">{{ t('hide_more_results') }}</template>
                <template v-else>{{ t('show_more_results') }}</template>
                <IconDropdownArrow width="14" height="6" />
            </button>
        </template>
        <div v-else class="no-results-msg">{{ t('no_results') }}</div>
    </div>
</template>
<script setup>
import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useWindowSize } from '@vueuse/core';

import PointOfSaleItem from '@/points-of-sale/components/PointOfSaleItem';

import IconDropdownArrow from '@@/icons/dropdown-arrow.svg';

const props = defineProps({
    pointsOfSale: {
        type: Array,
        default: () => [],
    },
    selectedPointIndex: {
        type: Number,
        default: 1,
    },
    meta: {
        type: Object,
        default: null
    }
});

const emit = defineEmits(['locatePoint']);

const { t } = useI18n({
    messages: {
        fr: {
            no_results: 'Aucun résultat',
            show_more_results: 'Voir plus de résultats',
            hide_more_results: 'Cacher les résultats',
            accessible_point_of_sale: 'Point de vente accessible',
            itinerary: 'Itinéraire',
            itinerary_to: 'Itinéraire vers',
            business_hours: 'Heures d’ouverture',
            website: 'Site web'
        },
        en: {
            no_results: 'No results',
            show_more_results: 'Show more results',
            hide_more_results: 'Hide more results',
            accessible_point_of_sale: 'Accessible point of sale',
            itinerary: 'Itinerary',
            itinerary_to: 'Itinerary to',
            business_hours: 'Business hours',
            website: 'Website'
        },
    },
});

const pointItemMessages = {
    accessible_point_of_sale: t('accessible_point_of_sale'),
    itinerary: t('itinerary'),
    itinerary_to: t('itinerary_to'),
    business_hours: t('business_hours'),
    website: t('website')
};

const moreResultsVisible = ref(false);
const visibleItemsCount = ref(4);

const pointList = ref();
const { width } = useWindowSize();

function calculateVisibleItems() {
    if (!pointList.value || moreResultsVisible.value) return;

    const listHeight = pointList.value.offsetHeight;
    const items = pointList.value.querySelectorAll('.point-item');
    if (items.length === 0) return;

    let accumulatedHeight = 0;
    let count = 0;

    for (let i = 0; i < items.length; i++) {
        const itemHeight = items[i].offsetHeight;
        if (accumulatedHeight + itemHeight <= listHeight) {
            accumulatedHeight += itemHeight;
            count++;
        } else {
            break;
        }
    }

    visibleItemsCount.value = Math.max(1, count);
}

async function recalculateVisibleItems() {
    await nextTick();
    calculateVisibleItems();
}

function showMoreResults() {
    moreResultsVisible.value = !moreResultsVisible.value;
    if (!moreResultsVisible.value && width.value >= 1024) {
        pointList.value.scrollTop = 0;
        setTimeout(() => {
            pointList.value.scrollIntoView({ behavior: 'smooth' });
        }, 300);
    }
}

function handleLocatePoint(pointOfSale) {
    emit('locatePoint', pointOfSale);
}

watch(() => props.pointsOfSale, () => {
    recalculateVisibleItems();
}, { deep: true });

onMounted(() => {
    recalculateVisibleItems();
    window.addEventListener('resize', recalculateVisibleItems);
});

onUnmounted(() => {
    window.removeEventListener('resize', recalculateVisibleItems);
});
</script>

<style lang="scss" scoped>
.point-list-wrapper {
    position: relative;
}
.point-list {
    position: relative;
    overflow-y: hidden;
    margin: 0;
    max-height: calc(100vh - 200px);
    @include breakpoint(large) {
        max-height: calc(100vh - 50px);
    }
    &.show-more-results {
        overflow-y: auto;
        @include breakpoint(large) {
            overflow-y: visible;
            max-height: none;
        }
        +.show-more-button {
            position: sticky;
            bottom: 0;
        }
    }
}
.point-item {
    border-top: 2px solid $color-black-2;
}
.no-results-msg {
    margin-top: 32px;
    text-align: center;
    color: $color-black-50;
}
.show-more-button {
    @include rem-typo(14px);
    width: 100%;
    padding: 16px;
    background-color: $color-white;
    color: $color-blue-60;
    border: 0;
    border-top: 2px solid $color-black-2;
    &.is-active {
        svg {
            transform: rotate(180deg);
        }
    }
    svg {
        margin-left: 8px;
        transition: all 300ms ease;
    }
}
</style>
